import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Column } from "@legal-500/legal500-design-system";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { RollingSubmission, RollingSubmissionStatus } from "@/api/rollingSubmissions.service";
import { editorialCategories } from "@/api/editorialCategories.service";
import { DataFilters } from "@/components/DataFilters/DataFilters";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import PermissionBanner from "@/components/Permissions/PermissionBanner";
import { PaginationControls } from "@/components/PaginationControls";
import { useFilters } from "@/hooks/useFilters";
import { usePublications } from "@/hooks/usePublications";
import { useRollingSubmissions } from "@/hooks/useRollingSubmissions";
import { useUrlParams } from "@/hooks/useUrlParams";
import { formatDateShort } from "@/utils/dateFormatter";
import { ROUTES } from "@/routes";
import styles from "../../CompanyUsers/CompanyUsers.module.css";
import localStyles from "./RollingSubmissionsList.module.css";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  pending_review: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  publish: "Published",
  draft: "Draft",
};

function getStatusClassName(status: string): string {
  switch (status) {
    case "pending":
    case "pending_review":
      return localStyles.statusPending;
    case "approved":
    case "publish":
      return localStyles.statusApproved;
    case "rejected":
      return localStyles.statusRejected;
    case "draft":
      return localStyles.statusDraft;
    default:
      return "";
  }
}

function RollingSubmissionsList() {
  const navigate = useNavigate();
  const { searchParams, updateUrl } = useUrlParams();

  const [filters, setFilters] = useState({
    publication: searchParams.get("publication") ?? "all",
    practiceArea: searchParams.get("practiceArea") ?? "all",
    region: searchParams.get("region") ?? "all",
    status: searchParams.get("status") ?? "all",
  });

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [activePublicationsOnly, setActivePublicationsOnly] = useState(true);
  const [pageSize, setPageSize] = useState(Number(searchParams.get("pageSize") ?? "50") || 50);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page") ?? "1") || 1);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
  });

  const publicationId =
    filters.publication === "all" ? undefined : Number.parseInt(filters.publication, 10);

  const regionId = filters.region === "all" ? undefined : Number.parseInt(filters.region, 10);

  const practiceAreaId =
    filters.practiceArea === "all" ? undefined : Number.parseInt(filters.practiceArea, 10);

  const isNeedsReviewFilter = filters.status === "needs_review";
  const statusFilter =
    filters.status === "all" || isNeedsReviewFilter
      ? undefined
      : (filters.status as RollingSubmissionStatus);

  const { data: publicationsData } = usePublications({
    status: "Active,Researching,Inactive,Archive",
  });

  const [countryOptions, setCountryOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [practiceAreas, setPracticeAreas] = useState<
    Array<{ id: number; name: string; practiceGroupName: string | null }>
  >([]);

  useEffect(() => {
    if (!publicationId) {
      setCountryOptions([]);
      return;
    }
    editorialCategories.getAll({ publicationId, type: "country", limit: 100 }).then(
      res => {
        const seen = new Map<number, string>();
        for (const ec of res.editorialCategories) {
          if (ec.countryId && ec.country && !seen.has(ec.countryId)) {
            seen.set(ec.countryId, ec.country);
          }
        }
        setCountryOptions(
          [...seen.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      },
      () => setCountryOptions([]),
    );
  }, [publicationId]);

  useEffect(() => {
    if (!publicationId || !regionId) {
      setPracticeAreas([]);
      return;
    }
    editorialCategories.getSubmissionBpas(publicationId, regionId).then(
      res => setPracticeAreas(res.practiceAreas),
      () => setPracticeAreas([]),
    );
  }, [publicationId, regionId]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
      updateUrl({ search: value, page: "1" });
    }, 300);
  };

  const { data, loading, error } = useRollingSubmissions({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    publicationId,
    practiceAreaId,
    regionId,
    status: statusFilter,
    hasPendingReview: isNeedsReviewFilter || undefined,
    search: debouncedSearch || undefined,
  });

  const submissions = data?.submissions ?? [];

  const pagination = data?.pagination ?? {
    total: 0,
    limit: pageSize,
    offset: 0,
  };

  const publicationOptions = useMemo(() => {
    const allPubs = publicationsData?.publications ?? [];
    const pubs = activePublicationsOnly
      ? allPubs.filter(
          (p: LegacyPublication) =>
            p.publicationStatus === "Active" || p.publicationStatus === "Researching",
        )
      : allPubs;
    return [
      { value: "all", label: "All publications" },
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication
          ? `${p.publication} (${p.edition ?? "n/a"})`
          : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
      })),
    ];
  }, [publicationsData?.publications, activePublicationsOnly]);

  const regionOptions = useMemo(() => {
    return [
      { value: "all", label: "All regions" },
      ...countryOptions.map(c => ({
        value: String(c.id),
        label: c.name,
      })),
    ];
  }, [countryOptions]);

  const practiceAreaOptions = useMemo(() => {
    return [
      { value: "all", label: "All practice areas" },
      ...practiceAreas.map(pa => ({
        value: String(pa.id),
        label: pa.practiceGroupName ? `${pa.practiceGroupName} > ${pa.name}` : pa.name,
      })),
    ];
  }, [practiceAreas]);

  const statusOptions = [
    { value: "all", label: "All statuses" },
    { value: "needs_review", label: "Needs review" },
    { value: "pending_review", label: "Pending" },
    { value: "publish", label: "Published" },
    { value: "rejected", label: "Rejected" },
  ];

  const FILTER_CONFIGS = [
    {
      key: "publication",
      label: "Publication",
      options: publicationOptions,
      searchable: true,
      labelExtra: (
        <label className={localStyles.checkboxLabel}>
          <input
            type="checkbox"
            checked={activePublicationsOnly}
            onChange={e => setActivePublicationsOnly(e.target.checked)}
          />
          Active &amp; Researching
        </label>
      ),
    },
    {
      key: "region",
      label: "Region",
      options: regionOptions,
      searchable: true,
      disabled: !publicationId,
      title: !publicationId ? "Select a publication first" : undefined,
    },
    {
      key: "practiceArea",
      label: "Practice Area",
      options: practiceAreaOptions,
      searchable: true,
      disabled: !publicationId || !regionId,
      title: !publicationId
        ? "Select a publication first"
        : !regionId
          ? "Select a region first"
          : undefined,
    },
    {
      key: "status",
      label: "Status",
      options: statusOptions,
    },
  ];

  const handleFilterChangeWithDeps = (key: string, value: string | number | boolean) => {
    setCurrentPage(1);
    updateUrl({ page: "1" });

    if (key === "publication") {
      handleFilterChange("publication", value);
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    if (key === "region") {
      handleFilterChange("region", value);
      handleFilterChange("practiceArea", "all");
      return;
    }

    handleFilterChange(key, value);
  };

  const handleClearAll = () => {
    setFilters({
      publication: "all",
      practiceArea: "all",
      region: "all",
      status: "all",
    });
    setCurrentPage(1);
    updateUrl({
      publication: "all",
      practiceArea: "all",
      region: "all",
      status: "all",
      page: "1",
    });
  };

  const handleClearFilterWithDeps = (key: string) => {
    setCurrentPage(1);

    if (key === "publication") {
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    handleClearFilter(key);
  };

  const enhancedActiveFilters = useMemo(() => {
    return activeFilters.map(filter => {
      if (filter.key === "publication") {
        const match = publicationOptions.find(opt => String(opt.value) === filters.publication);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "region") {
        const match = regionOptions.find(opt => String(opt.value) === filters.region);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "practiceArea") {
        const match = practiceAreaOptions.find(opt => String(opt.value) === filters.practiceArea);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "status") {
        const match = statusOptions.find(opt => String(opt.value) === filters.status);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      return {
        ...filter,
        displayValue: filter.displayValue ?? String(filter.value),
      } as const;
    });
  }, [
    activeFilters,
    filters.publication,
    filters.region,
    filters.practiceArea,
    filters.status,
    publicationOptions,
    regionOptions,
    practiceAreaOptions,
  ]);

  const columns: Column<RollingSubmission>[] = [
    {
      header: "Firm Name",
      accessor: "firmName",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Region",
      accessor: "country",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Practice Area",
      accessor: "practiceArea",
      render: (_value: unknown, row: RollingSubmission) =>
        row.practiceGroup
          ? `${row.practiceGroup} > ${row.practiceArea}`
          : row.practiceArea || "N/A",
    },
    {
      header: "Publication",
      accessor: "publication",
      render: (_value: unknown, row: RollingSubmission) => {
        const pub = publicationsData?.publications?.find(p => p.id === row.publicationId);
        if (pub) return `${pub.publication} (${pub.edition})`;
        return row.publication ?? "N/A";
      },
    },
    {
      header: "Last Updated",
      accessor: "lastUpdated",
      render: (value: unknown) => {
        if (!value) return "-";
        try {
          return formatDateShort(String(value));
        } catch (err) {
          console.error("Failed to format date:", value, err);
          return String(value);
        }
      },
    },
    {
      header: "Status",
      accessor: "status",
      render: (value: unknown) => {
        const status = String(value);
        return (
          <span className={`${localStyles.statusBadge} ${getStatusClassName(status)}`}>
            {STATUS_LABELS[status] ?? status}
          </span>
        );
      },
    },
    {
      header: "Review",
      accessor: "pendingReviewCount",
      render: (value: unknown) => {
        const pending = Number(value) || 0;
        if (pending === 0) return null;
        return <span className={localStyles.reviewPending}>{pending} to review</span>;
      },
    },
  ];

  const totalPages = Math.ceil(pagination.total / pageSize);
  const startIndex = pagination.offset;
  const endIndex = Math.min(startIndex + submissions.length, pagination.total);

  return (
    <div>
      <PermissionBanner scope="submissions" />
      <div className={styles.wrapper}>
        <PageHeader title="Rolling Submissions">
          <button
            type="button"
            className={localStyles.createBtn}
            onClick={() => navigate(ROUTES.submissions.rollingSubmissionCreate)}
          >
            + Create Rolling Submission
          </button>
        </PageHeader>

        <div className={localStyles.searchBar}>
          <svg
            className={localStyles.searchIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by firm name…"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            className={localStyles.searchInput}
          />
        </div>

        <DataFilters
          filters={FILTER_CONFIGS}
          values={filters}
          onChange={handleFilterChangeWithDeps}
          activeFilters={enhancedActiveFilters}
          onClearFilter={handleClearFilterWithDeps}
          onClearAll={handleClearAll}
        />

        <div className={localStyles.toolbarRow}>
          <div />
          <div className={localStyles.rowsPerPage}>
            <label htmlFor="pageSize" className={localStyles.rowsPerPageLabel}>
              Show
            </label>
            <select
              id="pageSize"
              className={localStyles.rowsPerPageSelect}
              value={pageSize}
              onChange={e => {
                const val = Number(e.target.value);
                setPageSize(val);
                setCurrentPage(1);
                updateUrl({ pageSize: String(val), page: "1" });
              }}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && <LoadingState message="Loading rolling submissions..." />}

        {error && !loading && (
          <EmptyState
            variant="error"
            title="Failed to load rolling submissions"
            message={error.message}
            action={{
              label: "Retry",
              onClick: () => window.location.reload(),
            }}
          />
        )}

        {!loading && !error && submissions.length === 0 && (
          <EmptyState
            variant="empty"
            title="No rolling submissions found"
            message="There are no rolling submissions matching your filters."
          />
        )}

        {!loading && !error && submissions.length > 0 && (
          <>
            <DataTable
              data={submissions}
              columns={columns}
              size="small"
              maxHeight="55vh"
              rowKey="id"
              onRowClick={row => navigate(ROUTES.submissions.rollingSubmissionDetail(row.id))}
            />

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalCount={pagination.total}
              itemName="submissions"
              onPageChange={page => {
                setCurrentPage(page);
                updateUrl({ page: String(page) });
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default RollingSubmissionsList;
